import {
  DirectionalHint,
  FontIcon,
  HoverCard,
  HoverCardType,
  IRenderFunction,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { OutboundCommStatusesEnumText } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { ClinicalReminderCommContactRecordDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ClinicalActivityStatus } from "@shared-types/clinical/clinical-activity-status.type.ts";
import { When } from "@ui-components/withPerm.tsx";

import { ClinicalActivityTableRow } from "./types/clinical-activity-table.type.ts";

export interface FollowupReminderCurrentStatusBadgeProps {
  reminder: ClinicalActivityTableRow;
  contactRecord: ClinicalReminderCommContactRecordDto;
  sortedContactRecords: ClinicalReminderCommContactRecordDto[];
  itemNumber?: number;
  showRepliesForCollection?: boolean;
  useHoverCard?: boolean;
  onRenderPlainCard?: IRenderFunction<any>;
}

export const FollowupReminderCurrentStatusBadge: React.FC<
  FollowupReminderCurrentStatusBadgeProps
> = ({
  reminder,
  itemNumber,
  sortedContactRecords,
  contactRecord,
  showRepliesForCollection,
  onRenderPlainCard,
  useHoverCard
}) => {
  const greyStatuses = [
    ClinicalActivityStatus.Declined,
    ClinicalActivityStatus.NoLongerRequired,
    ClinicalActivityStatus.NoResponse
  ];

  const isGreyStatus = greyStatuses.find(
    x => x === reminder.clinicalReminderComm?.reminderStatus
  );

  const getBadgeColor = (reminder: ClinicalActivityTableRow) => {
    let color = TextBadgeColor.grey;
    const sentReminders = reminder.clinicalReminderComm?.contactRecords.length;
    if (isGreyStatus) {
      color = TextBadgeColor.grey;
    } else if (sentReminders) {
      color = sentReminders > 1 ? TextBadgeColor.red : TextBadgeColor.yellow;
    }

    return color;
  };

  const getBadgeText = (item: ClinicalReminderCommContactRecordDto) => {
    let statusText: string | undefined = "";

    if (isGreyStatus) {
      statusText = reminder.activity.activityStatus;
    } else {
      statusText = `${
        OutboundCommStatusesEnumText[
          item.status as keyof typeof OutboundCommStatusesEnumText
        ]
      }`;
    }

    let badgeText = "";

    if (itemNumber) {
      badgeText = `${statusText} ${itemNumber}`;
    } else {
      badgeText = `${statusText} ${
        !isGreyStatus ? sortedContactRecords.length : ""
      }`;
    }

    return badgeText;
  };

  const showReplyBubble =
    contactRecord.replies ||
    (showRepliesForCollection &&
      sortedContactRecords.find(x => x.replies && x.replies.length > 0));

  const renderContents = () => {
    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 4 }}
        verticalAlign="center"
        horizontalAlign="center"
      >
        <Stack>{getBadgeText(contactRecord)}</Stack>
        <When permission={Permission.ClinicalReminderReplyAllowed}>
          {showReplyBubble && (
            <Stack>
              <FontIcon
                iconName="SkypeMessage"
                styles={{ root: { fontSize: 12 } }}
              />
            </Stack>
          )}
        </When>
      </Stack>
    );
  };

  return (
    <Stack styles={{ root: { justifyContent: "top", height: "100%" } }}>
      <TextBadge
        badgeSize={TextBadgeSize.small}
        badgeColor={getBadgeColor(reminder)}
      >
        {useHoverCard && onRenderPlainCard ? (
          <HoverCard
            type={HoverCardType.plain}
            plainCardProps={{
              calloutProps: {
                directionalHint: DirectionalHint.rightCenter,
                directionalHintFixed: true,
                isBeakVisible: true,
                calloutMinWidth: 430,
                gapSpace: 5
              },
              onRenderPlainCard: () => onRenderPlainCard()
            }}
          >
            {renderContents()}
          </HoverCard>
        ) : (
          <>{renderContents()}</>
        )}
      </TextBadge>
    </Stack>
  );
};
